import React from 'react'
import PropTypes from 'prop-types'
import useForm from 'react-hook-form'
import { RHFInput } from 'react-hook-form-input'

import { makeStyles } from '@material-ui/core/styles'
import {
  Paper,
  Box,
  TextField,
  Button,
  Collapse,
  Typography,
  Divider,
} from '@material-ui/core'

import InputPhone from 'components/InputPhone'
import InputCnpj from 'components/InputCnpj'
import InputCpf from 'components/InputCpf'
import schemas from './../../../../schemas'
import styles from './styles'
import helpers from 'helpers'

const useStyles = makeStyles(styles)

const UploadLogo = ({ defaultImage, onChange = () => {} }) => {
  const classes = useStyles()
  const [file, setFile] = React.useState(null)
  const [fileURL, setFileURL] = React.useState(null)

  const image = React.useMemo(() => fileURL || defaultImage, [
    defaultImage,
    fileURL,
  ])

  React.useEffect(() => {
    if (!file) {
      setFile(null)
      return
    }

    const fileReader = new FileReader()

    fileReader.onload = event => {
      setFileURL(event.target.result)
    }

    fileReader.readAsDataURL(file)
  }, [file])

  const handleChange = event => {
    const file = event.target.files[0] || null

    setFile(file)
    onChange(event, file)
  }

  return (
    <div className={classes.file}>
      <Typography variant="h6">Logotipo</Typography>

      <div className={classes.imageContainer}>
        {image && (
          <img
            src={image}
            alt="Logotipo do vendedor"
            className={classes.image}
          />
        )}

        <ul className={classes.imageList}>
          <li>
            <Typography color="textSecondary">
              - Tamanho recomendado: 60px x 200px
            </Typography>
          </li>
          <li>
            <Typography color="textSecondary">
              - Formato: jpeg, jpg ou png
            </Typography>
          </li>
          <li>
            <Typography color="textSecondary">
              - Fundo branco ou transparente
            </Typography>
          </li>
        </ul>
      </div>

      <div className={classes.fileContainer}>
        <input
          accept="image/*"
          id="input-file"
          type="file"
          className={classes.inputFile}
          onChange={handleChange}
        />

        <label htmlFor="input-file">
          <Button
            variant="text"
            color="secondary"
            component="span"
            className={classes.buttonFile}
          >
            {defaultImage ? 'Alterar logotipo' : 'Adicionar logotipo'}
          </Button>
        </label>
      </div>
    </div>
  )
}

const SettingsAccountForm = ({
  seller,
  onSubmit,
  onChangeLogo = () => {},
  handleClose = () => {},
}) => {
  const classes = useStyles()
  const { register, handleSubmit, setValue, errors, watch } = useForm({
    validationSchema: schemas.account,
    defaultValues: {
      ...seller,
      ...(helpers.people.isPerson(seller.kind)
        ? { companyName: '', cnpj: '' }
        : {}),
      cpf: seller.cpf || '',
      phone: helpers.people.parsePhoneApi(seller.contact),
      municipalRegistration: seller.municipalRegistration || '',
    },
  })
  const values = watch()

  const handleChangeKind = () => {
    if (helpers.people.isCompany(values.kind)) {
      setValue('cnpj', '')
      setValue('companyName', '')
    } else {
      setValue('cnpj', seller.cnpj || '')
      setValue('companyName', seller.companyName || '')
    }
  }

  return (
    <Paper>
      <form onSubmit={handleSubmit(onSubmit)} id="settings-account-form">
        <Box p={2}>
          <RHFInput
            as={
              <TextField
                label="Nome"
                margin="dense"
                error={!!errors.name}
                helperText={errors && errors.name && errors.name.message}
                fullWidth
              />
            }
            name="name"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField
                label="Email"
                margin="dense"
                error={!!errors.email}
                helperText={errors && errors.email && errors.email.message}
                disabled={true}
                fullWidth
              />
            }
            name="email"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField
                label="Nome fantasia"
                margin="dense"
                error={!!errors.tradeName}
                helperText={
                  errors && errors.tradeName && errors.tradeName.message
                }
                fullWidth
              />
            }
            name="tradeName"
            register={register}
            setValue={setValue}
          />
          <Collapse in={helpers.people.isCompany(values.kind)}>
            <RHFInput
              as={
                <TextField
                  label="Razão social"
                  margin="dense"
                  error={!!errors.companyName}
                  helperText={
                    errors && errors.companyName && errors.companyName.message
                  }
                  fullWidth
                />
              }
              name="companyName"
              register={register}
              setValue={setValue}
            />
            <RHFInput
              as={
                <InputCnpj
                  label="CNPJ"
                  margin="dense"
                  error={!!errors.cnpj}
                  disabled={seller.cnpj && values.cnpj ? true : false}
                  helperText={errors && errors.cnpj && errors.cnpj.message}
                  fullWidth
                />
              }
              name="cnpj"
              register={register}
              setValue={setValue}
            />
          </Collapse>
          <RHFInput
            as={
              <InputCpf
                label="CPF"
                margin="dense"
                error={!!errors.cpf}
                helperText={errors && errors.cpf && errors.cpf.message}
                disabled={true}
                fullWidth
              />
            }
            name="cpf"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField
                label="Tipo"
                margin="dense"
                select={true}
                SelectProps={{ native: true }}
                fullWidth
                disabled={helpers.people.isCompany(seller.kind)}
              >
                <option value="C">Pessoa Jurídica</option>
                <option value="P">Pessoa Física</option>
              </TextField>
            }
            name="kind"
            register={register}
            setValue={(...params) => {
              handleChangeKind()
              setValue(...params)
            }}
          />
          <RHFInput
            as={
              <InputPhone
                label="Telefone"
                margin="dense"
                inputProps={{
                  maxLength: 15,
                }}
                error={!!errors.phone}
                helperText={errors && errors.phone && errors.phone.message}
                fullWidth
              />
            }
            name="phone"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField label="Inscrição municipal" margin="dense" fullWidth />
            }
            name="municipalRegistration"
            register={register}
            setValue={setValue}
          />
          <TextField
            label="Código do vendedor"
            margin="dense"
            value={seller.code}
            fullWidth
            disabled
          />

          <UploadLogo defaultImage={seller.logo} onChange={onChangeLogo} />
        </Box>
      </form>

      <Divider />

      <Box p={2}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          form="settings-account-form"
          className={classes.btnSave}
        >
          Salvar
        </Button>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => handleClose(false)}
        >
          Cancelar
        </Button>
      </Box>
    </Paper>
  )
}

SettingsAccountForm.propTypes = {
  seller: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default SettingsAccountForm
